import React, { useState, useEffect, useRef } from 'react';
import './diarioObraHome.css';
import FiltroObras from './FiltroObras';
import CardObra from './CardObra';
import NovaObraButton from './NovaObraButton';
import ConfiguracaoButton from './ConfiguracaoButton';
import Header from '../Header';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/ApiService';
import { useTheme } from '../../contexts/ThemeContext';
import { toast } from 'react-hot-toast';

const DiarioObraHome = () => {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const { darkMode } = useTheme();
  const [obras, setObras] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [obrasPendentes, setObrasPendentes] = useState([]);
  const [mostrarPendencias, setMostrarPendencias] = useState(false);
  const [carregandoPendencias, setCarregandoPendencias] = useState(false);
  const [filtro, setFiltro] = useState({
    termo: '',
    status: 'todos',
    ordenacao: 'recentes'
  });
  const configuracaoBtnRef = useRef(null);
  const [modalEditarAberto, setModalEditarAberto] = useState(false);
  const [obraEmEdicao, setObraEmEdicao] = useState(null);

  // Verificar se o usuário é um cliente
  const isClient = currentUser?.role === 'client';

  useEffect(() => {
    const carregarDados = async () => {
      await carregarConfiguracoes();
      await buscarObras();
      if (isClient) {
        await buscarObrasPendentes();
      }
    };
    
    carregarDados();
  }, [isClient]);

  const carregarConfiguracoes = async () => {
    if (!currentUser || !currentUser.empresa) return;
    
    try {
      const resultado = await apiService.getDiarioObraConfiguracao();
      
      if (!resultado.success) {
        setErro('Não foi possível carregar as configurações');
      }
    } catch (error) {
      setErro('Erro ao carregar configurações');
    }
  };

  const buscarObras = async () => {
    try {
      setCarregando(true);
      setErro(null);
      
      const resposta = await apiService.getDiarioObras();
      
      if (!resposta.success) {
        throw new Error(resposta.error || 'Erro ao buscar obras');
      }
      
      const listaObras = resposta.data?.obras || [];
      setObras(listaObras);
    } catch (erro) {
      setErro('Não foi possível carregar as obras. Tente novamente mais tarde.');
    } finally {
      setCarregando(false);
    }
  };

  // Função para buscar obras pendentes de compartilhamento
  const buscarObrasPendentes = async () => {
    if (!isClient) return;
    
    try {
      setCarregandoPendencias(true);
      const response = await apiService.listarObrasPendentes();
      
      if (response.success) {
        setObrasPendentes(response.data?.obras || []);
      } else {
        console.error('Erro ao buscar obras pendentes:', response.error);
      }
    } catch (error) {
      console.error('Erro ao buscar obras pendentes:', error);
    } finally {
      setCarregandoPendencias(false);
    }
  };

  // Função para aceitar compartilhamento de obra
  const aceitarCompartilhamento = async (obraId, codigoCompartilhamento) => {
    try {
      const response = await apiService.aceitarCompartilhamentoObra(obraId, codigoCompartilhamento);
      
      if (response.success) {
        toast.success('Obra aceita com sucesso!');
        // Atualizar lista de obras pendentes
        setObrasPendentes(prev => prev.filter(obra => obra.id !== obraId));
        // Atualizar lista de obras
        await buscarObras();
      } else {
        toast.error(response.error || 'Erro ao aceitar obra');
      }
    } catch (error) {
      toast.error('Erro ao aceitar obra');
      console.error('Erro:', error);
    }
  };

  // Função para rejeitar compartilhamento de obra
  const rejeitarCompartilhamento = async (obraId, codigoCompartilhamento) => {
    try {
      const response = await apiService.rejeitarCompartilhamentoObra(obraId, codigoCompartilhamento);
      
      if (response.success) {
        toast.success('Obra rejeitada com sucesso!');
        // Atualizar lista de obras pendentes
        setObrasPendentes(prev => prev.filter(obra => obra.id !== obraId));
      } else {
        toast.error(response.error || 'Erro ao rejeitar obra');
      }
    } catch (error) {
      toast.error('Erro ao rejeitar obra');
      console.error('Erro:', error);
    }
  };

  const handleFiltroChange = (novoFiltro) => {
    setFiltro({
      ...filtro,
      ...novoFiltro
    });
  };

  const adicionarObra = (novaObra) => {
    setObras(obrasAtuais => [novaObra, ...obrasAtuais]);
  };

  const handleExcluirObra = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta obra?')) {
      return;
    }

    try {
      const resultado = await apiService.deleteDiarioObra(id);
      
      if (!resultado.success) {
        throw new Error(resultado.error || 'Erro ao excluir obra');
      }
      
      setObras(obras.filter(obra => obra.id !== id));
      toast.success('Obra excluída com sucesso!');
    } catch (error) {
      console.error('Erro ao excluir obra:', error);
      toast.error(error.message || 'Erro ao excluir obra. Tente novamente.');
    }
  };

  const handleAtualizarObra = (obraAtualizada) => {
    setObras(obrasAtuais => 
      obrasAtuais.map(obra => 
        obra.id === obraAtualizada.id ? obraAtualizada : obra
      )
    );
  };

  const handleLogout = () => {
    logout()
      .then(() => {
        navigate('/login');
      })
      .catch(error => {
        setErro('Erro ao fazer logout');
      });
  };

  const handleObraClick = (obra) => {
    navigate(`/diario-obra/${obra.id}`);
  };

  const obrasExibidas = obras.filter(obra => {
    const termoBusca = filtro.termo.toLowerCase();
    const nomeBate = obra.nome?.toLowerCase().includes(termoBusca);
    const clienteBate = obra.cliente?.toLowerCase().includes(termoBusca);
    const enderecoBate = obra.endereco?.toLowerCase().includes(termoBusca);
    
    if (termoBusca && !nomeBate && !clienteBate && !enderecoBate) {
      return false;
    }
    
    if (filtro.status !== 'todos' && obra.status?.toLowerCase() !== filtro.status) {
      return false;
    }
    
    return true;
  });
  
  const obrasOrdenadas = [...obrasExibidas].sort((a, b) => {
    if (filtro.ordenacao === 'recentes') {
      return new Date(b.dataCriacao) - new Date(a.dataCriacao);
    } else if (filtro.ordenacao === 'antigas') {
      return new Date(a.dataCriacao) - new Date(b.dataCriacao);
    } else if (filtro.ordenacao === 'alfabetica') {
      return a.nome?.localeCompare(b.nome);
    }
    return 0;
  });

  const handleEditarObra = (obra) => {
    setObraEmEdicao(obra);
    setModalEditarAberto(true);
  };

  const handleSalvarEdicao = async (obraEditada) => {
    try {
      const resultado = await apiService.updateDiarioObra(obraEditada.id, obraEditada);
      
      if (!resultado.success) {
        throw new Error(resultado.error || 'Erro ao atualizar obra');
      }
      
      // Atualizar a lista de obras
      setObras(obras.map(o => o.id === obraEditada.id ? obraEditada : o));
      setModalEditarAberto(false);
      setObraEmEdicao(null);
      toast.success('Obra atualizada com sucesso!');
    } catch (error) {
      console.error('Erro ao editar obra:', error);
      toast.error(error.message || 'Erro ao editar obra. Tente novamente.');
    }
  };

  return (
    <div className={`diario-obra-page ${darkMode ? 'dark-mode' : ''}`}>
      <Header 
        onLogout={handleLogout}
        showActionButtons={false}
      />
      
      <div className="diario-obra-container">
        <div className="diario-obra-header">
          <h1>Diário de Obras</h1>
          <div className="diario-obra-acoes">
            {isClient && obrasPendentes.length > 0 && (
              <button 
                className="btn-pendencias"
                onClick={() => setMostrarPendencias(!mostrarPendencias)}
                title={`${obrasPendentes.length} obra(s) pendente(s)`}
              >
                <i className="fas fa-bell"></i>
                <span>Pendências</span>
                <span className="badge-pendencias">{obrasPendentes.length}</span>
              </button>
            )}
            <button 
              className="config-button" 
              onClick={() => navigate('/configuracao-conta')}
              title="Configurações"
            >
              <i className="fas fa-cog"></i>
              <span>Configurações</span>
            </button>
            <NovaObraButton onSave={adicionarObra} />
          </div>
        </div>

        {/* Modal de Pendências */}
        {mostrarPendencias && isClient && (
          <div className="modal-pendencias">
            <div className="modal-pendencias-content">
              <div className="modal-pendencias-header">
                <h2>Obras Pendentes</h2>
                <button 
                  className="btn-fechar"
                  onClick={() => setMostrarPendencias(false)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              
              {carregandoPendencias ? (
                <div className="carregando-pendencias">
                  <div className="loader"></div>
                  <p>Carregando pendências...</p>
                </div>
              ) : obrasPendentes.length === 0 ? (
                <p className="sem-pendencias">Não há obras pendentes</p>
              ) : (
                <div className="lista-pendencias">
                  {obrasPendentes.map(obra => (
                    <div key={obra.id} className="pendencia-item">
                      <div className="pendencia-info">
                        <h3>{obra.nome}</h3>
                        <p>Compartilhada por: {obra.proprietario?.nome || obra.proprietario?.email}</p>
                        <p>Data: {new Date(obra.dataCompartilhamento).toLocaleDateString('pt-BR')}</p>
                        <div className="permissoes">
                          <strong>Permissões:</strong>
                          <ul>
                            {obra.permissoes?.map(permissao => (
                              <li key={permissao}>
                                {permissao === 'visualizar' ? 'Visualizar' :
                                 permissao === 'editar' ? 'Editar' :
                                 permissao === 'comentar' ? 'Comentar' : permissao}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      <div className="pendencia-acoes">
                        <button 
                          className="btn-aceitar"
                          onClick={() => aceitarCompartilhamento(obra.id, obra.codigoCompartilhamento)}
                        >
                          <i className="fas fa-check"></i> Aceitar
                        </button>
                        <button 
                          className="btn-rejeitar"
                          onClick={() => rejeitarCompartilhamento(obra.id, obra.codigoCompartilhamento)}
                        >
                          <i className="fas fa-times"></i> Rejeitar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <FiltroObras filtro={filtro} onFiltroChange={handleFiltroChange} darkMode={darkMode} />
        
        {erro && (
          <div className={`erro-container ${darkMode ? 'dark-mode' : ''}`}>
            <i className="fas fa-exclamation-circle"></i>
            <p>{erro}</p>
            <button className="btn-tentar-novamente" onClick={buscarObras}>
              Tentar Novamente
            </button>
          </div>
        )}

        {carregando ? (
          <div className="carregando-container">
            <div className="loader"></div>
            <p>Carregando obras...</p>
          </div>
        ) : (
          <div className="obras-grid">
            {obras.map(obra => (
              <CardObra
                key={obra.id}
                obra={obra}
                onExcluir={handleExcluirObra}
                onUpdate={handleAtualizarObra}
                onEdit={handleEditarObra}
                darkMode={darkMode}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modal de edição */}
      {modalEditarAberto && obraEmEdicao && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h2>Editar Obra</h2>
              <button className="btn-fechar" onClick={() => setModalEditarAberto(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form className="form-nova-obra" onSubmit={(e) => {
              e.preventDefault();
              handleSalvarEdicao(obraEmEdicao);
            }}>
              <div className="form-group">
                <label htmlFor="nome">Nome da Obra</label>
                <input
                  type="text"
                  id="nome"
                  name="nome"
                  value={obraEmEdicao.nome}
                  onChange={(e) => setObraEmEdicao({...obraEmEdicao, nome: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="cliente">Cliente</label>
                <input
                  type="text"
                  id="cliente"
                  name="cliente"
                  value={obraEmEdicao.cliente}
                  onChange={(e) => setObraEmEdicao({...obraEmEdicao, cliente: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label htmlFor="endereco">Endereço</label>
                <input
                  type="text"
                  id="endereco"
                  name="endereco"
                  value={obraEmEdicao.endereco}
                  onChange={(e) => setObraEmEdicao({...obraEmEdicao, endereco: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label htmlFor="responsavel">Responsável</label>
                <input
                  type="text"
                  id="responsavel"
                  name="responsavel"
                  value={obraEmEdicao.responsavel}
                  onChange={(e) => setObraEmEdicao({...obraEmEdicao, responsavel: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label htmlFor="dataInicio">Data de Início</label>
                <input
                  type="date"
                  id="dataInicio"
                  name="dataInicio"
                  value={obraEmEdicao.dataInicio ? new Date(obraEmEdicao.dataInicio).toISOString().split('T')[0] : ''}
                  onChange={(e) => setObraEmEdicao({...obraEmEdicao, dataInicio: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label htmlFor="dataPrevisaoFim">Previsão de Término</label>
                <input
                  type="date"
                  id="dataPrevisaoFim"
                  name="dataPrevisaoFim"
                  value={obraEmEdicao.dataPrevisaoFim ? new Date(obraEmEdicao.dataPrevisaoFim).toISOString().split('T')[0] : ''}
                  onChange={(e) => setObraEmEdicao({...obraEmEdicao, dataPrevisaoFim: e.target.value})}
                />
              </div>
              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn-cancelar"
                  onClick={() => setModalEditarAberto(false)}
                >
                  Cancelar
                </button>
                <button 
                  type="submit" 
                  className="btn-salvar"
                >
                  Salvar Alterações
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiarioObraHome; 